package er.prateek.crossly.ui.share

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.challenge_offer_time
import crossly.shared.generated.resources.challenge_result_won
import crossly.shared.generated.resources.web_share_challenge_from
import crossly.shared.generated.resources.web_share_invite_from
import crossly.shared.generated.resources.web_share_ios_hint
import crossly.shared.generated.resources.web_share_play_store
import crossly.shared.generated.resources.web_share_result_from
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.share.ShareFunnel
import er.prateek.crossly.share.ShareLinks
import er.prateek.crossly.share.SharePayload
import er.prateek.crossly.share.WebClient
import er.prateek.crossly.share.browserEnvironment
import er.prateek.crossly.share.formatSpokenDuration
import er.prateek.crossly.share.openExternalUrl
import er.prateek.crossly.share.webClientFromUserAgent
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonSize
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.IconLogoMark
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import org.jetbrains.compose.resources.stringResource

@Composable
fun WebShareLandingScreen(
    payload: SharePayload,
    token: String,
) {
    val colors = MaterialTheme.game
    val android = webClientFromUserAgent(browserEnvironment().userAgent) == WebClient.ANDROID
    var puzzleLabel by remember(payload.puzzleId) { mutableStateOf<String?>(null) }

    LaunchedEffect(payload.puzzleId) {
        if (payload.isChallenge || payload.isResult) {
            puzzleLabel = AppContainer.shareManager.puzzleLabel(payload.puzzleId)
        }
    }

    val title = when {
        payload.isResult && payload.opponentTimeMs > 0 && payload.timeMs < payload.opponentTimeMs ->
            stringResource(
                Res.string.challenge_result_won,
                payload.name,
                formatSpokenDuration(payload.opponentTimeMs - payload.timeMs),
            )
        payload.isResult -> stringResource(Res.string.web_share_result_from, payload.name)
        payload.isChallenge -> stringResource(Res.string.web_share_challenge_from, payload.name)
        else -> stringResource(Res.string.web_share_invite_from, payload.name)
    }

    GameScreen {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = Spacing.lg),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(Spacing.lg, Alignment.CenterVertically),
        ) {
            IconLogoMark(
                size = 72.dp,
                tint = colors.ink,
                accentTint = colors.accent,
                blockTint = colors.cellBlock,
            )
            Text(
                text = title,
                style = MaterialTheme.typography.headlineSmall,
                color = colors.ink,
                textAlign = TextAlign.Center,
                modifier = Modifier.widthIn(max = 420.dp),
            )
            if (payload.isChallenge || payload.isResult) {
                Text(
                    text = puzzleLabel ?: payload.puzzleId,
                    style = MaterialTheme.typography.titleMedium,
                    color = colors.inkMuted,
                    textAlign = TextAlign.Center,
                )
            }
            if (payload.isChallenge && payload.timeMs > 0) {
                Text(
                    text = stringResource(
                        Res.string.challenge_offer_time,
                        formatSpokenDuration(payload.timeMs),
                    ),
                    style = MaterialTheme.typography.bodyMedium,
                    color = colors.inkMuted,
                    textAlign = TextAlign.Center,
                )
            }
            if (android) {
                GameButton(
                    text = stringResource(Res.string.web_share_play_store),
                    onClick = {
                        val medium = when {
                            payload.isResult -> "result"
                            payload.isChallenge -> "challenge"
                            else -> "app-share"
                        }
                        ShareFunnel.log(
                            AppContainer.analytics,
                            AnalyticsEvents.SHARE_PLAY_STORE_CLICK,
                            payload,
                        )
                        openExternalUrl(ShareLinks.playStoreUrl(token, medium))
                    },
                    style = GameButtonStyle.PRIMARY,
                    size = GameButtonSize.LARGE,
                    fillWidth = true,
                    modifier = Modifier.widthIn(max = 420.dp).fillMaxWidth(),
                )
            } else {
                Text(
                    text = stringResource(Res.string.web_share_ios_hint),
                    style = MaterialTheme.typography.bodyMedium,
                    color = colors.inkMuted,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.widthIn(max = 420.dp),
                )
            }
        }
    }
}
